import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth, API } from '@/App';

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }

    const checkPaymentStatus = async () => {
      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const token = localStorage.getItem('session_token');
        const response = await fetch(`${API}/payments/checkout/status/${sessionId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          if (data.payment_status === 'paid') {
            setStatus('success');
          } else if (data.status === 'expired') {
            setStatus('failed');
          } else {
            setStatus('processing');
            setTimeout(() => {
              setAttempts(prev => prev + 1);
            }, 2000);
          }
        } else {
          setStatus('error');
        }
      } catch (error) {
        console.error('Payment status check error:', error);
        setStatus('error');
      }
    };

    checkPaymentStatus();
  }, [sessionId, navigate, attempts]);

  if (status === 'checking' || status === 'processing') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 pt-16" data-testid="checkout-processing">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <h1 className="text-2xl font-heading font-bold text-white mb-2">Processing Payment...</h1>
          <p className="text-text-secondary font-body">Please wait while we confirm your payment</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 pt-16" data-testid="checkout-success-page">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-md w-full"
        >
          <div className="bg-background-paper rounded-2xl shadow-xl p-8 text-center border border-primary/20">
            <div className="mb-6">
              <CheckCircle2 className="w-20 h-20 text-status-success mx-auto animate-float" data-testid="success-icon" />
            </div>
            <h1 className="text-3xl font-heading font-bold text-white mb-4" data-testid="success-title">Payment Successful!</h1>
            <p className="text-text-secondary font-body mb-8" data-testid="success-message">
              Thank you for your order. We've received your payment and will start preparing your order right away.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-3 text-lg shadow-lg shadow-primary/30 hover:shadow-primary/50 button-primary"
                data-testid="view-orders-button"
              >
                <Package className="w-5 h-5 mr-2" />
                View My Orders
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full border-2 border-white/20 text-white hover:bg-white/5 hover:border-primary rounded-full py-3"
                data-testid="back-home-button"
              >
                Back to Home
              </Button>
            </div>
            <div className="mt-8 p-4 bg-primary/10 rounded-xl border border-primary/20">
              <p className="text-sm text-text-secondary font-body">
                <span className="text-primary font-semibold">15% of your purchase</span> goes towards fighting food poverty and supporting food charities. Thank you for making a difference!
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 pt-16" data-testid="checkout-error">
      <div className="max-w-md w-full bg-background-paper rounded-2xl shadow-xl p-8 text-center border border-primary/20">
        <h1 className="text-2xl font-heading font-bold text-white mb-4">Payment Issue</h1>
        <p className="text-text-secondary font-body mb-6">
          {status === 'timeout' ? 'Payment verification is taking longer than expected. Please check your email for confirmation.' : 'There was an issue verifying your payment. Please contact support if the amount was charged.'}
        </p>
        <Button
          onClick={() => navigate('/')}
          className="bg-primary hover:bg-primary-hover text-white rounded-full px-8 py-3"
        >
          Back to Home
        </Button>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
